import { createSlice } from '@reduxjs/toolkit';
import { commonMessages } from 'messages';
import { Snackbar } from '@agco-onedigital/ecosystem-lib-web-components';

import { getMachineThunk, deleteMachineThunk, editMachineThunk } from './thunks';
import { uploadDeviceImageThunk } from './thunks/uploadDeviceImageThunk';
import { deleteDeviceImageThunk } from './thunks/deleteDeviceImageThunk';
import { createMachineNoteThunk } from './thunks/createMachineNoteThunk';
import { updateMachineNoteThunk } from './thunks/updateMachineNoteThunk';
import { deleteMachineNoteThunk } from './thunks/deleteMachineNoteThunk';
import { IMachineDetailsState } from './types';

const messages = commonMessages.general;

const initialState: IMachineDetailsState = {};

const machineDetailsSlice = createSlice({
  name: 'machineDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getMachineThunk.rejected, (state, action) => {
      state.isLoading = false;
      state.isFailedToLoad = true;
      state.isNotesUpdating = false;

      // Extract error code from the rejected action payload
      const errorCode = (action.payload as { errorCode?: string })?.errorCode;
      state.errorCode = errorCode;

      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(getMachineThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload;
      if (action.payload) {
        state.data.notes =
          action.payload.notes
            ?.slice(0)
            .sort((a, b) => new Date(b.updateDateTime).getTime() - new Date(a.updateDateTime).getTime()) || [];
      }
      state.isNotesUpdating = false;
      state.errorCode = undefined; // Clear error code on successful fetch
    });
    builder.addCase(getMachineThunk.pending, state => {
      state.isLoading = true;
      state.isFailedToLoad = false;
      state.isNotesUpdating = true;
      state.errorCode = undefined; // Clear error code when starting new fetch
    });

    builder.addCase(deleteMachineThunk.rejected, state => {
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(deleteMachineThunk.fulfilled, (state, action) => {
      state.data = undefined;
    });
    builder.addCase(deleteMachineThunk.pending, state => {
      state.isFailedToLoad = false;
    });

    builder.addCase(editMachineThunk.rejected, state => {
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(editMachineThunk.fulfilled, (state, action) => {
      state.data = {
        ...state.data,
        ...action.payload,
      };
    });
    builder.addCase(editMachineThunk.pending, state => {
      state.isFailedToLoad = false;
    });
    builder.addCase(uploadDeviceImageThunk.rejected, state => {
      state.isImageUpdating = false;
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(uploadDeviceImageThunk.fulfilled, (state, action) => {
      state.uploadImage = action.payload;
      state.isImageUpdating = false;
    });
    builder.addCase(uploadDeviceImageThunk.pending, state => {
      state.isImageUpdating = true;
      state.isFailedToLoad = false;
    });
    builder.addCase(deleteDeviceImageThunk.fulfilled, (state, action) => {
      state.uploadImage = action.payload;
    });
    builder.addCase(deleteDeviceImageThunk.pending, state => {
      state.isFailedToLoad = false;
    });
    builder.addCase(createMachineNoteThunk.rejected, state => {
      state.isNotesUpdating = false;
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(createMachineNoteThunk.fulfilled, (state, action) => {
      state.isNotesUpdating = false;
    });
    builder.addCase(createMachineNoteThunk.pending, state => {
      state.isNotesUpdating = true;
    });

    builder.addCase(updateMachineNoteThunk.rejected, state => {
      state.isNotesUpdating = false;
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(updateMachineNoteThunk.fulfilled, (state, action) => {
      state.isNotesUpdating = false;
    });
    builder.addCase(updateMachineNoteThunk.pending, state => {
      state.isNotesUpdating = true;
    });
    builder.addCase(deleteMachineNoteThunk.rejected, state => {
      state.isNotesUpdating = false;
      Snackbar.showSnack({
        title: messages.apiError,
        buttonTitle: messages.reload,
        type: 'error',
        duration: 5000,
        onButtonClick: () => window.location.reload(),
      });
    });
    builder.addCase(deleteMachineNoteThunk.fulfilled, (state, action) => {
      state.isNotesUpdating = false;
    });
    builder.addCase(deleteMachineNoteThunk.pending, state => {
      state.isNotesUpdating = true;
    });
  },
});

const machineDetailsReducer = machineDetailsSlice.reducer;

export default machineDetailsReducer;
